/**
 * Contractor Service
 * API calls for contractor management
 */

import apiClient from '../client';
import type { Contractor, PaginatedResponse } from '../../types';

interface ContractorFilters {
  page?: number;
  size?: number;
}

const contractorService = {
  /**
   * Get all contractors with pagination
   */
  getContractors: async (filters: ContractorFilters = {}) => {
    const { page = 0, size = 20 } = filters;
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    const response = await apiClient.get<PaginatedResponse<Contractor>>(
      `/contractors?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get contractor by ID
   */
  getContractorById: async (id: string) => {
    const response = await apiClient.get<Contractor>(`/contractors/${id}`);
    return response.data;
  },

  /**
   * Create new contractor
   */
  createContractor: async (data: Partial<Contractor>) => {
    const response = await apiClient.post<Contractor>('/contractors', data);
    return response.data;
  },

  /**
   * Update contractor
   */
  updateContractor: async (id: string, data: Partial<Contractor>) => {
    const response = await apiClient.put<Contractor>(`/contractors/${id}`, data);
    return response.data;
  },

  activateContractor: async (id: string) => {
    await apiClient.patch(`/contractors/${id}/activate`);
  },

  /**
   * Delete contractor
   */
  deleteContractor: async (id: string) => {
    await apiClient.delete(`/contractors/${id}`);
  },

  search: async (params: { page?: number; size?: number; search?: string; laborType?: string; active?: boolean } = {}) => {
    const { page = 0, size = 10, search, laborType, active } = params;
    const qs = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (search) qs.set('search', search);
    if (laborType) qs.set('laborType', laborType);
    if (active !== undefined) qs.set('active', String(active));
    const response = await apiClient.get<PaginatedResponse<Contractor>>(`/contractors/search?${qs}`);
    return response.data;
  },
};

export default contractorService;
